import { useQuery } from "@tanstack/react-query";
import { Spin } from "antd";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { apiFetch } from "./api/client";
import type { MainIndexState } from "./api/types";
import { useAuth } from "./auth/AuthProvider";
import { AppLayout } from "./layout/AppLayout";
import { ArchivePage } from "./pages/ArchivePage";
import { AssignRolePage } from "./pages/AssignRolePage";
import { CircumscriptionContactsPage } from "./pages/CircumscriptionContactsPage";
import { CircumscriptionManagementPage } from "./pages/CircumscriptionManagementPage";
import { CircumscriptionSettingsPage } from "./pages/CircumscriptionSettingsPage";
import { CollaborationsPage } from "./pages/CollaborationsPage";
import { ConfirmChangesPage } from "./pages/ConfirmChangesPage";
import { CongregationContactsPage } from "./pages/CongregationContactsPage";
import { CongregationManagementPage } from "./pages/CongregationManagementPage";
import { CongregationSettingsPage } from "./pages/CongregationSettingsPage";
import { CongregationSpeakerStatisticsPage } from "./pages/CongregationSpeakerStatisticsPage";
import { CongregationSpeechStatisticsPage } from "./pages/CongregationSpeechStatisticsPage";
import { CredentialsPage } from "./pages/CredentialsPage";
import { InviteCongregationsPage } from "./pages/InviteCongregationsPage";
import { LanguageManagementPage } from "./pages/LanguageManagementPage";
import { LocalAssignationPage } from "./pages/LocalAssignationPage";
import { LocalCoordinatorSettingsPage } from "./pages/LocalCoordinatorSettingsPage";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { MeetingTimesPage } from "./pages/MeetingTimesPage";
import { MessagesPage } from "./pages/MessagesPage";
import { NewsPage } from "./pages/NewsPage";
import { NotMigratedPage } from "./pages/NotMigratedPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { InstructionsPage } from "./pages/public/InstructionsPage";
import { RegisterPage } from "./pages/public/RegisterPage";
import { ResetPasswordPage } from "./pages/public/ResetPasswordPage";
import { TokenLandingPage } from "./pages/public/TokenLandingPage";
import { RoleContactSettingsPage } from "./pages/RoleContactSettingsPage";
import { RoleManagementPage } from "./pages/RoleManagementPage";
import { SiteConfigurationPage } from "./pages/SiteConfigurationPage";
import {
  SpeakerAssignationPage,
  SpeakerCongregationEventsPage,
} from "./pages/SpeakerAssignationPage";
import { SpeakerDataPage } from "./pages/SpeakerDataPage";
import { SpeakerManagementPage } from "./pages/SpeakerManagementPage";
import { SpeakerRequestPage } from "./pages/SpeakerRequestPage";
import { SpeakerCongregationStatisticsPage } from "./pages/SpeakerCongregationStatisticsPage";
import { SpeakerSettingsPage } from "./pages/SpeakerSettingsPage";
import { SpeakerSpeechStatisticsPage } from "./pages/SpeakerSpeechStatisticsPage";
import { SpeechAssignationPage } from "./pages/SpeechAssignationPage";
import { SubstitutionPage } from "./pages/SubstitutionPage";
import { SpeechDataPage } from "./pages/SpeechDataPage";
import { SpeechManagementPage } from "./pages/SpeechManagementPage";
import { SpeechSelectionPage } from "./pages/SpeechSelectionPage";
import { UserSettingsPage } from "./pages/UserSettingsPage";
import { ZoneAssignationPage } from "./pages/ZoneAssignationPage";

function RequireAuth({ children }: { children: ReactNode }) {
  const { status, privacyPolicyRequired, pendingInvitationsDismissed } = useAuth();
  const location = useLocation();

  // Two things stand between the login and the application, and the server owns
  // both answers. The query is deliberately not skipped while the policy is
  // pending: its 403 is what tells the client about the policy in the first
  // place (see toError in api/client.ts).
  const landing = useQuery({
    queryKey: ["main", "index"],
    queryFn: () => apiFetch<MainIndexState>("/main/index"),
    enabled: status === "authenticated",
  });

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // The API refuses everything else until the policy has been accepted, so
  // there is no point letting the user navigate anywhere first. The policy
  // screen itself is routed outside this guard.
  if (privacyPolicyRequired) {
    return <Navigate to="/privacy-policy" replace />;
  }

  // Waiting here rather than rendering the app and pulling it away a moment
  // later. It is one small request, cached for the whole navigation.
  if (landing.isPending) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  // Unlike the policy this only redirects until it is dismissed — the
  // invitations block nothing, they just should not be missed after logging in.
  if (landing.data?.pending_invitations && !pendingInvitationsDismissed) {
    return <Navigate to="/confirm-changes" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const { status } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={status === "authenticated" ? <Navigate to="/" replace /> : <LoginPage />}
      />

      {/* Reachable without an account, and reachable while logged in too: the
          links in the mails carry a one-time token and have to work in whatever
          browser the mail is opened in. The paths are the ones the old
          application used — SpeechplanMailer builds its links from them. */}
      <Route path="/welcome/new_user" element={<RegisterPage />} />
      <Route path="/users/password/new" element={<InstructionsPage flow="password" />} />
      <Route path="/users/password/edit" element={<ResetPasswordPage />} />
      <Route path="/users/confirmation/new" element={<InstructionsPage flow="confirmation" />} />
      <Route path="/users/confirmation" element={<TokenLandingPage flow="confirmation" />} />
      <Route path="/users/unlock/new" element={<InstructionsPage flow="unlock" />} />
      <Route path="/users/unlock" element={<TokenLandingPage flow="unlock" />} />

      {/* Deliberately outside AppLayout: while the acceptance is pending the API
          answers 403 for everything except this screen, so a menu would offer
          nothing but refusals. The old application hid its sidebar here too. */}
      <Route
        path="/privacy-policy"
        element={
          status === "authenticated" ? <PrivacyPolicyPage /> : <Navigate to="/login" replace />
        }
      />

      {/* Also without the shell, for the same reason: this is a step on the way
          in, not a place in the navigation. */}
      <Route
        path="/confirm-changes"
        element={
          status === "authenticated" ? <ConfirmChangesPage /> : <Navigate to="/login" replace />
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />

        {/* Every sidebar entry routes to /:controller/:action — the path has to
            match the controller/action pair MainController#sidebar reports for
            the entry, not the API route the screen calls. The remaining screens
            are filled in one by one as they are migrated. */}
        {/* No sidebar entry — the old application kept the account in its user
            menu, which is where the header link sits now. */}
        <Route path="/main/password" element={<CredentialsPage />} />

        <Route path="/main/settings" element={<UserSettingsPage />} />
        <Route path="/role/manage_roles" element={<RoleManagementPage />} />
        <Route path="/invitation/assign_new_role" element={<AssignRolePage />} />
        <Route path="/circumscription/contacts" element={<CircumscriptionContactsPage />} />
        <Route path="/circumscription/manage" element={<CongregationManagementPage />} />
        <Route
          path="/circumscription/circumscription_settings"
          element={<CircumscriptionSettingsPage />}
        />
        <Route
          path="/circumscription/invite_congregations"
          element={<InviteCongregationsPage />}
        />
        <Route path="/zone_assignation/assignations" element={<ZoneAssignationPage />} />
        <Route path="/local_assignation/assignations" element={<LocalAssignationPage />} />
        <Route
          path="/local_assignation/speech_assignations"
          element={<SpeechAssignationPage />}
        />
        <Route
          path="/local_assignation/local_speaker_substitution"
          element={<SubstitutionPage />}
        />
        <Route path="/local_assignation/speaker_requests" element={<SpeakerRequestPage />} />
        <Route path="/speaker_assignation/assignations" element={<SpeakerAssignationPage />} />
        <Route
          path="/speaker_assignation/congregation_assignations"
          element={<SpeakerCongregationEventsPage />}
        />
        <Route path="/congregation/contacts" element={<CongregationContactsPage />} />
        <Route path="/congregation/congregation_times" element={<MeetingTimesPage />} />
        <Route
          path="/congregation/congregation_settings"
          element={<CongregationSettingsPage />}
        />
        {/* The same two statistics, read from the congregation's side. */}
        <Route
          path="/congregation/speaker_statistics"
          element={<CongregationSpeakerStatisticsPage />}
        />
        <Route
          path="/congregation/speech_statistics"
          element={<CongregationSpeechStatisticsPage />}
        />
        <Route path="/speaker/manage" element={<SpeakerManagementPage />} />
        <Route path="/speech/index" element={<SpeechManagementPage />} />
        <Route path="/speech/speech_selection" element={<SpeechSelectionPage />} />
        <Route path="/speaker/speaker_settings" element={<SpeakerSettingsPage />} />
        {/* The two statistics grids of a speaker — where they have been and
            what they have held. Read-only history, so they sit next to the
            speaker's own screens rather than under the assignments. */}
        <Route
          path="/speaker/congregation_statistics"
          element={<SpeakerCongregationStatisticsPage />}
        />
        <Route path="/speaker/speech_statistics" element={<SpeakerSpeechStatisticsPage />} />
        {/* Two sidebar entries, one screen: same two grids, different role. */}
        <Route
          path="/collaboration/speaker_collaborations"
          element={<CollaborationsPage scope="speaker" />}
        />
        <Route
          path="/collaboration/local_coord_collaborations"
          element={<CollaborationsPage scope="local_coordinator" />}
        />
        <Route
          path="/congregation/local_coordinator_settings"
          element={<LocalCoordinatorSettingsPage />}
        />
        <Route path="/webmaster/circumscriptions" element={<CircumscriptionManagementPage />} />
        <Route path="/webmaster/languages" element={<LanguageManagementPage />} />
        <Route path="/configuration/configurations" element={<SiteConfigurationPage />} />
        {/* Three sidebar entries, one screen: each role's own past calendar. */}
        <Route
          path="/circumscription/circumscription_archive"
          element={<ArchivePage scope="circumscription" />}
        />
        <Route
          path="/congregation/congregation_archive"
          element={<ArchivePage scope="congregation" />}
        />
        <Route path="/speaker/speaker_archive" element={<ArchivePage scope="speaker" />} />
        {/* Two sidebar entries, one screen: both roles carry the same settings,
            only the endpoint differs. */}
        <Route
          path="/webmaster/webmaster_settings"
          element={<RoleContactSettingsPage scope="webmaster" />}
        />
        <Route
          path="/circumscription/zone_coordinator_settings"
          element={<RoleContactSettingsPage scope="zone_coordinator" />}
        />
        <Route path="/message/index" element={<MessagesPage />} />
        {/* Two sidebar entries, one screen: the scope decides which endpoints
            it talks to and whether publishing per circumscription is offered. */}
        <Route path="/news/site_news" element={<NewsPage scope="webmaster" />} />
        <Route
          path="/news/circumscription_news"
          element={<NewsPage scope="zone_coordinator" />}
        />
        <Route path="/data/speech_data" element={<SpeechDataPage />} />
        <Route path="/data/speaker_data" element={<SpeakerDataPage />} />

        <Route path="*" element={<NotMigratedPage />} />
      </Route>
    </Routes>
  );
}
